import { useIsMobile } from 'lib/hooks/useIsBreakpointWidth';
import { SailthruFormattedItem } from 'lib/sailthru';
import { isEmpty } from 'lodash-es';
import { useLayoutEffect, useState, type RefObject } from 'react';
import {
  AD_HEIGHT,
  RIGHT_COLUMN_PADDING,
  SEE_ALL_LINK_HEIGHT,
  TALK_ITEM_HEIGHT
} from '../constants';

interface UseLayoutCalculationsOptions {
  leftColumnRef: RefObject<HTMLDivElement | null>;
  rightColumnRef: RefObject<HTMLDivElement | null>;
  contentData: SailthruFormattedItem[];
  ribbonTalks: SailthruFormattedItem[];
  showAdUnit: boolean;
  isAdBlocked: boolean;
  isAdFilled: boolean;
  isAdLoading: boolean;
  hasSeeAllLink: boolean;
}

export function useLayoutCalculations({
  leftColumnRef,
  rightColumnRef,
  contentData,
  ribbonTalks,
  showAdUnit,
  isAdBlocked,
  isAdFilled,
  isAdLoading,
  hasSeeAllLink
}: UseLayoutCalculationsOptions) {
  const [visibleTalkCount, setVisibleTalkCount] = useState(4);
  const [shouldShowAdInRibbon, setShouldShowAdInRibbon] = useState(false);

  const { isDesktop, isTablet } = useIsMobile({
    checkDevice: false
  });

  useLayoutEffect(() => {
    if ((!isDesktop && !isTablet) || isEmpty(contentData)) return;

    const calculateVisibleTalks = () => {
      if (!leftColumnRef.current || !rightColumnRef.current) return;

      const leftHeight = leftColumnRef.current.offsetHeight;
      const currentWidth = window.innerWidth;

      // Ad is visible if:
      // 1. Enabled in slice
      // 2. Not blocked
      // 3. Within correct width ranges
      // 4. Actually filled (or still loading)
      const isAdVisible =
        showAdUnit &&
        !isAdBlocked &&
        (isAdFilled || isAdLoading) &&
        (currentWidth < 808 || currentWidth >= 1055);

      if (leftHeight === 0) return;

      // Include ad height if it's loading or filled
      const adHeight = isAdVisible ? AD_HEIGHT : 0;
      const seeAllLinkHeight = hasSeeAllLink ? SEE_ALL_LINK_HEIGHT : 0;

      const availableHeight = leftHeight - RIGHT_COLUMN_PADDING;

      let optimalCount;
      if (isAdVisible && ribbonTalks.length >= 2) {
        setShouldShowAdInRibbon(true);
        const spaceForTalks = availableHeight - adHeight - seeAllLinkHeight;
        const maxTalksWithAd = Math.floor(spaceForTalks / TALK_ITEM_HEIGHT);

        let minTalksWithAd = 2;
        if (currentWidth >= 1055) {
          minTalksWithAd = 3;
        }

        optimalCount = Math.min(
          Math.max(maxTalksWithAd, minTalksWithAd),
          ribbonTalks.length
        );
      } else {
        setShouldShowAdInRibbon(false);
        const availableForTalks = availableHeight - seeAllLinkHeight;
        const maxTalks = Math.floor(availableForTalks / TALK_ITEM_HEIGHT);

        let minTalks = 3;
        if (currentWidth >= 1025 && currentWidth < 1055) {
          minTalks = 6;
        } else if (currentWidth >= 1055) {
          minTalks = 6;
        }

        optimalCount = Math.min(
          Math.max(maxTalks, minTalks),
          ribbonTalks.length
        );
      }

      setVisibleTalkCount(optimalCount);
    };

    const timeouts = [100, 300, 500].map(delay =>
      setTimeout(calculateVisibleTalks, delay)
    );

    window.addEventListener('resize', calculateVisibleTalks);

    return () => {
      timeouts.forEach(clearTimeout);
      window.removeEventListener('resize', calculateVisibleTalks);
    };
  }, [
    isDesktop,
    isTablet,
    contentData,
    showAdUnit,
    hasSeeAllLink,
    ribbonTalks.length,
    isAdBlocked,
    isAdFilled,
    isAdLoading,
    leftColumnRef,
    rightColumnRef
  ]);

  return {
    visibleTalkCount,
    shouldShowAdInRibbon
  };
}
